export interface ContentPair {
  ja: string;
  en: string;
}

export interface BusinessService {
  id: string;
  title: ContentPair;
  description: ContentPair;
  features: ContentPair[];
  link?: string;
  linkText?: ContentPair;
}

export interface FounderMessage {
  name: ContentPair;
  role: ContentPair;
  content: ContentPair[];
}

export interface CompanyPhilosophy {
  mainTitle: ContentPair;
  description: ContentPair;
  points: { title: ContentPair; detail: ContentPair }[];
}

export interface ComplianceInfo {
  title: ContentPair;
  statement: ContentPair;
  points: ContentPair[];
}

export const BUSINESS_SERVICES: BusinessService[] = [
  {
    id: "cat-hotel",
    title: {
      ja: "猫専用ペットホテル運営（主事業）",
      en: "Cat Hotel Operations (Primary Business)"
    },
    description: {
      ja: "大阪拠点の実店舗運営。猫の習性を熟知したスタッフによる、ストレスフリーな滞在環境を提供します。",
      en: "Osaka-based physical facility operation. We provide a stress-free environment for cats, managed by staff with deep expertise in feline behavior."
    },
    features: [
      { ja: "完全猫専用設計", en: "Exclusively for cats" },
      { ja: "少頭数制による手厚いケア", en: "Small-group care system" },
      { ja: "長期滞在・シニア猫対応", en: "Long-term stay & senior cat support" }
    ],
    link: "https://catretreat-nekojarashi.jp",
    linkText: {
      ja: "公式ブランドサイト →",
      en: "Official Brand Website →"
    }
  },
  {
    id: "pet-products",
    title: {
      ja: "ペット関連商品企画販売",
      en: "Pet Product Planning & Sales"
    },
    description: {
      ja: "動物福祉の観点に基づき、ペットの生活の質を向上させる高品質な製品を企画・販売しています。",
      en: "Based on animal welfare principles, we plan and sell high-quality products that improve the quality of life for pets."
    },
    features: [
      { ja: "独自ブランド商品の企画", en: "In-house brand product planning" },
      { ja: "厳選されたペット用品のセレクト", en: "Curated selection of pet supplies" },
      { ja: "会員向け長期ケアサポート", en: "Long-term care support for members" }
    ]
  },
  {
    id: "cross-border",
    title: {
      ja: "跨境ブランド支援",
      en: "Cross-border Brand Support"
    },
    description: {
      ja: "自社店舗運営で積み重ねたブランド構築と顧客体験設計の経験を基盤とし、日・中・英三言語環境における市場理解を活かしたブランド展開支援を実務レベルで提供します。",
      en: "Based on brand building and customer experience design expertise accumulated through our own facility operations, we provide practical brand expansion support leveraging market understanding across Japanese, Chinese, and English-speaking environments."
    },
    features: [
      { ja: "日本市場への海外ブランド進出支援", en: "Japan market entry consulting" },
      { ja: "中国・アジア市場への展開戦略策定", en: "China & Asia expansion strategy" },
      { ja: "ブランドローカライズ・代理運営", en: "Brand localization & representation" },
      { ja: "多言語コミュニケーションサポート", en: "Multilingual communication support" }
    ]
  },
  {
    id: "marketing",
    title: {
      ja: "マーケティング支援",
      en: "Marketing Support"
    },
    description: {
      ja: "デジタル領域におけるブランド価値の最大化を、戦略立案から運用までワンストップでサポートします。",
      en: "We provide one-stop support for maximizing brand value in the digital space, from strategy formulation to operations."
    },
    features: [
      { ja: "企業・ブランドサイト制作", en: "Corporate & brand website production" },
      { ja: "SNS運用・コンサルティング", en: "SNS operations & consulting" },
      { ja: "広告運用・マーケティング支援", en: "Advertising & marketing support" }
    ]
  }
];

export const FOUNDER_MESSAGE: FounderMessage = {
  name: {
    ja: "秦 佳玲",
    en: "ROXY QIN"
  },
  role: {
    ja: "代表取締役",
    en: "Representative Director"
  },
  content: [
    {
      ja: "実店舗運営を基盤とし、日本国内での長期的な事業継続を前提に法人経営を行っています。大阪を拠点とした猫専用ペットホテル「猫じゃらし Cat Retreat」の運営を主事業として、地域社会との共存を大切にしながら事業を展開しております。",
      en: "As an Osaka-based corporation, we operate physical businesses with a commitment to long-term sustainability and community integration. Our primary business is the operation of 'Cat Retreat Nekojarashi', a cat-exclusive boarding facility that serves as the foundation of our corporate activities."
    },
    {
      ja: "日本・中国・英語圈での実務経験を通じて習得した現場経験を基盤とし、ブランドの海外展開支援を行っています。自社店舗運営で積み重ねたブランド構築と顧客体験設計のノウハウを、他社のブランド支援に活かしています。",
      en: "Leveraging trilingual cultural expertise across Japanese, Chinese, and English-speaking markets, we provide practical cross-border brand expansion and localization support. This is not mere consulting, but hands-on assistance based on our actual business operation experience."
    },
    {
      ja: "日本の法令遵守を徹底し、銀行や関係機関からの信頼を得られる健全な企業運営を継続し、地域社会の発展に貢献してまいります。",
      en: "We maintain strict compliance with Japanese regulations and operate with the trust of financial institutions and regulatory bodies, contributing to the development of our local community through sound business practices."
    }
  ]
};

export const COMPANY_PHILOSOPHY: CompanyPhilosophy = {
  mainTitle: {
    ja: "誠実な事業運営と多文化理解を通じて、長期的な価値を創出する。",
    en: "Creating long-term value through sincere operations and multicultural understanding."
  },
  description: {
    ja: "短期的利益ではなく、文化や市場の違いを理解した上で、持続可能な事業運営を目指します。",
    en: "We aim for sustainable business operations based on an understanding of cultural and market differences, rather than pursuing short-term gains."
  },
  points: [
    {
      title: { ja: "動物福祉への貢献", en: "Commitment to Animal Welfare" },
      detail: { ja: "ペット事業を通じ、動物たちがより豊かに暮らせる社会の実現に寄与します。", en: "Through our pet businesses, we contribute to a society where animals can live more fulfilling lives." }
    },
    {
      title: { ja: "多文化共生", en: "Multicultural Symbiosis" },
      detail: { ja: "中日英の架け橋として、異なる文化間の理解を深めるビジネスを推進します。", en: "As a bridge between Japan, China, and the West, we promote businesses that deepen cross-cultural understanding." }
    },
    {
      title: { ja: "誠実な経営", en: "Integrity in Management" },
      detail: { ja: "全てのステークホルダーに対し、透明性の高い誠実な事業運営を徹底します。", en: "We ensure highly transparent and sincere business operations for all stakeholders." }
    }
  ]
};

export const COMPLIANCE_INFO: ComplianceInfo = {
  title: {
    ja: "コンプライアンス体制",
    en: "Compliance Framework"
  },
  statement: {
    ja: "株式会社M.M.Kは、日本国内の法令を厳格に遵守し、健全な企業活動を継続するためのガバナンス体制を構築しています。",
    en: "M.M.K Co., Ltd. strictly adheres to Japanese laws and has established a governance framework to ensure sound corporate activities."
  },
  points: [
    {
      ja: "適切な税務申告および納税義務の履行",
      en: "Proper tax filing and fulfillment of tax obligations"
    },
    {
      ja: "労働基準法に基づく適切な労務管理",
      en: "Appropriate labor management based on the Labor Standards Act"
    },
    {
      ja: "反社会的勢力との関係遮断の徹底",
      en: "Strict exclusion of relationships with anti-social forces"
    },
    {
      ja: "個人情報保護法に基づくデータ管理",
      en: "Data management based on the Personal Information Protection Act"
    }
  ]
};